import React, { createContext, useContext, useReducer, useEffect, useRef, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, UserRole } from '../types';
import { authService } from '../services/api';
import toast from 'react-hot-toast';
import axios from 'axios';

// Tipos del contexto de autenticación
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: Partial<User> }
  | { type: 'CLEAR_ERROR' };

interface AuthContextType extends AuthState {
  login: (cedula: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  clearError: () => void;
  isAdmin: boolean;
}

// Estado inicial
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      return { ...state, user: action.payload, isAuthenticated: true, isLoading: false, error: null };
    case 'AUTH_FAILURE':
      return { ...state, user: null, isAuthenticated: false, isLoading: false, error: action.payload };
    case 'LOGOUT':
      return { ...state, user: null, isAuthenticated: false, isLoading: false, error: null };
    case 'UPDATE_USER':
      return { ...state, user: state.user ? { ...state.user, ...action.payload } : null };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Crear el contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Proveedor
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const initializationRef = useRef(false);

  // Verificar token al cargar la aplicación - SOLO UNA VEZ
  useEffect(() => {
    const initializeAuth = async () => {
      // Prevenir múltiples inicializaciones
      if (initializationRef.current) return;
      initializationRef.current = true;

      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('user');

      if (token) {
        try {
          dispatch({ type: 'AUTH_START' });

          // Aplicar token a axios
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          if (userData) {
            dispatch({ type: 'AUTH_SUCCESS', payload: JSON.parse(userData) });
          }

          const result = await authService.verifyToken(token);
          if (result.valid && result.user) {
            localStorage.setItem('user', JSON.stringify(result.user));
            dispatch({ type: 'AUTH_SUCCESS', payload: result.user });
          } else {
            logout();
          }
        } catch (error) {
          console.error('Error verificando token:', error);
          logout();
        }
      } else {
        dispatch({ type: 'LOGOUT' });
      }
    };

    initializeAuth();
  }, []); // Sin dependencias - solo se ejecuta una vez

  // Función de login - MEJORADA
  const login = async (cedula: string, password: string): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await authService.login({ cedula, password });
      // console.log('Respuesta del login:', response);

      // Guardar token + usuario
      localStorage.setItem('authToken', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      // Aplicar token a axios
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.token}`;

      dispatch({ type: 'AUTH_SUCCESS', payload: response.user });

      toast.success(`¡Bienvenido, ${response.user.name}!`);
    } catch (error: any) {
      console.error('Error en login:', error);

      let errorMessage = 'Error de autenticación';

      if (error.response?.status === 401) {
        errorMessage = 'Credenciales incorrectas. Verifica tu cédula y contraseña.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Demasiados intentos. Por favor espera un momento.';
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });

      throw error;
    }
  };

  // Función de logout
  const logout = (): void => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    dispatch({ type: 'LOGOUT' });
    toast.success('Sesión cerrada exitosamente');
  };

  // Función para actualizar datos de usuario
  const updateUser = (userData: Partial<User>): void => {
    dispatch({ type: 'UPDATE_USER', payload: userData });

    const currentUser = localStorage.getItem('user');
    if (currentUser) {
      const user = JSON.parse(currentUser);
      const updatedUser = { ...user, ...userData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  // Función para limpiar errores
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const isAdmin = state.user?.role === UserRole.ADMIN;

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    updateUser,
    clearError,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hooks de protección MEJORADOS
export const useRequireAuth = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectedRef = useRef(false);

  useEffect(() => {
    // Solo redirigir una vez y cuando no esté cargando
    if (!isLoading && !isAuthenticated && !redirectedRef.current) {
      redirectedRef.current = true;
      // Guardar la página actual para redirigir después del login
      const from = location.pathname;
      navigate('/login', { state: { from }, replace: true });
    }

    // Reset el flag si el usuario se autentica
    if (isAuthenticated) {
      redirectedRef.current = false;
    }
  }, [isAuthenticated, isLoading, navigate, location]);

  return { isAuthenticated, isLoading };
};

export const useRequireAdmin = () => {
  const { isAuthenticated, isLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !redirectedRef.current) {
      if (!isAuthenticated) {
        redirectedRef.current = true;
        navigate('/login', { replace: true });
      } else if (!isAdmin) {
        redirectedRef.current = true;
        navigate('/dashboard', { replace: true });
        toast.error('No tienes permisos para acceder a esta sección');
      }
    }

    // Reset el flag si el estado cambia favorablemente
    if (isAuthenticated && isAdmin) {
      redirectedRef.current = false;
    }
  }, [isAuthenticated, isLoading, isAdmin, navigate]);

  return { isAuthenticated, isLoading, isAdmin };
};